import { motion } from 'framer-motion';
import Scene3D from '@/components/Scene3D';
import { ArrowRight, Diamond, Box, Layers } from 'lucide-react';
import { useCheckout } from '@/components/CheckoutContext';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function HeroSection() {
  const { open } = useCheckout();
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-16"
      style={{ background: '#FFF7CD' }}
    >
      <Scene3D />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-0">
        <div className="max-w-3xl">
          <motion.span
            custom={0}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="uppercase text-xs tracking-[0.25em] font-bold text-[#c88a5e] mb-6 block font-body"
            data-testid="hero-overline"
          >
            <Diamond className="inline-block w-3 h-3 mr-2 -mt-0.5" />
            DesignCarat Bundle
          </motion.span>

          <motion.h1
            custom={1}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="font-heading font-black text-[#111] leading-[0.92] tracking-tight mb-8"
            style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}
            data-testid="hero-headline"
          >
            4,000+{' '}
            <span className="block">Production-Ready</span>
            <span className="block text-[#c88a5e]">STL Designs</span>
          </motion.h1>

          <motion.p
            custom={2}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-[#555] text-lg md:text-xl leading-relaxed font-body font-medium max-w-xl mb-10"
            data-testid="hero-subheadline"
          >
            Access the ultimate{' '}
            <span className="text-[#111] font-semibold">900GB+ Master Drive</span>{' '}
            packed with premium 3D models, ready to print and ship.
          </motion.p>

          <motion.div
            custom={3}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <button
              data-testid="hero-cta-button"
              className="btn-accent cta-pulse inline-flex items-center gap-3 text-base sm:text-lg px-10 py-5 group"
              onClick={() => open('hero_cta')}
            >
              🚀 Grab Bundle Now &bull; ₹999
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>

          <motion.div
            custom={4}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="flex flex-wrap items-center gap-6 mt-10 text-[#777] text-sm font-body"
            data-testid="hero-trust-badges"
          >
            <span className="inline-flex items-center gap-2">
              <Box className="w-4 h-4 text-[#FDC3A1]" />
              <span className="font-mono text-xs">4,000+</span> STL Files
            </span>
            <span className="inline-flex items-center gap-2">
              <Layers className="w-4 h-4 text-[#FDC3A1]" />
              <span className="font-mono text-xs">900GB+</span> Content
            </span>
            <span className="inline-flex items-center gap-2">
              <Diamond className="w-4 h-4 text-[#FDC3A1]" />
              Instant Download
            </span>
          </motion.div>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 h-32 z-[2]"
        style={{ background: 'linear-gradient(to top, #FFFFFF, transparent)' }}
      />
    </section>
  );
}
